import { AuthLayout } from '@/components/auth/AuthLayout';
import { getPasswordRules, PasswordStrengthGauge } from '@/components/auth/PasswordStrengthGauge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useI18n } from '@/context/I18nProvider';
import { getClerkErrorMessage } from '@/utils/clerk-errors';
import { useSignIn } from '@clerk/nextjs';
import { CircularLoader } from '@/components/ui/CircularLoader';
import { ArrowLeft, ArrowRight, Eye, EyeOff } from '@/components/ui/icons/Icons';
import Link from 'next/link';
import { FormEvent, useState } from 'react';

type Step = 'email' | 'reset';

export default function ForgotPasswordPage() {
  const { t } = useI18n();
  const { signIn, setActive, isLoaded } = useSignIn();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const passwordsMatch = !confirmPassword || newPassword === confirmPassword;

  const allRulesMet = newPassword ? getPasswordRules(newPassword).every((r) => r.met) : false;
  const isResetFormValid =
    code.length === 6 &&
    allRulesMet &&
    confirmPassword.trim() !== '' &&
    newPassword === confirmPassword;

  const handleSendCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;

    setIsLoading(true);
    setError('');

    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      });
      setStep('reset');
    } catch (err: unknown) {
      setError(getClerkErrorMessage(err, t));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;

    if (newPassword !== confirmPassword) {
      setError(t('auth.forgotPasswordFlow.passwordsDoNotMatch'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code,
        password: newPassword,
      });

      if (result.status === 'complete' && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        window.location.href = '/conversations';
        return;
      }
    } catch (err: unknown) {
      setError(getClerkErrorMessage(err, t));
    }
    setIsLoading(false);
  };

  return (
    <AuthLayout>
      <div className="space-y-6">
        {' '}
        {/* Header */}
        <div className="flex flex-col items-center space-y-3">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
            {t('auth.welcomeTo')} <span className="text-primary">Orsay</span>
          </h1>
          <p className="text-gray-500 text-sm text-center">{t('auth.subtitle')}</p>
        </div>
        {step === 'email' ? (
          <>
            {/* Back to sign in */}
            <Link
              href="/sign-in"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="size-4" />
              {t('auth.forgotPasswordFlow.backToSignIn')}
            </Link>

            <div className="space-y-2">
              <h2 className="text-2xl font-bold tracking-tight">
                {t('auth.forgotPasswordFlow.title')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('auth.forgotPasswordFlow.subtitle')}
              </p>
            </div>

            <form onSubmit={handleSendCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  className="h-12 rounded-lg border-gray-300 bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  autoComplete="email"
                  required
                />
              </div>

              {error && (
                <div className="text-sm text-destructive text-center bg-destructive/10 rounded-lg p-3">
                  {error}
                </div>
              )}

              {/* Clerk Smart CAPTCHA widget mount point for bot protection */}
              <div id="clerk-captcha" className="mt-2 flex justify-center" />

              <Button
                type="submit"
                className="w-full h-12 rounded-full text-sm font-medium"
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularLoader className="size-4" />
                ) : (
                  <>
                    {t('auth.forgotPasswordFlow.sendCode')}
                    <ArrowRight className="size-4" />
                  </>
                )}
              </Button>
            </form>
          </>
        ) : (
          <>
            {/* Back to email step */}
            <button
              type="button"
              onClick={() => {
                setStep('email');
                setError('');
                setCode('');
                setNewPassword('');
                setConfirmPassword('');
              }}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="size-4" />
              {t('common.back')}
            </button>

            <div className="space-y-2">
              <h2 className="text-2xl font-bold tracking-tight">
                {t('auth.forgotPasswordFlow.resetTitle')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('auth.forgotPasswordFlow.codeSent', { email })}
              </p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">{t('auth.verificationCode')}</Label>
                <Input
                  id="code"
                  type="text"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '');
                    setCode(v);
                    setError('');
                  }}
                  className="h-12 rounded-lg border-gray-300 bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">{t('auth.forgotPasswordFlow.newPassword')}</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setError('');
                    }}
                    className="h-12 rounded-lg pr-10 border-gray-300 bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
                <PasswordStrengthGauge password={newPassword} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">
                  {t('auth.forgotPasswordFlow.confirmPassword')}
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setError('');
                    }}
                    className="h-12 rounded-lg pr-10 border-gray-300 bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </button>
                </div>
                {!passwordsMatch && (
                  <p className="text-xs text-destructive mt-1">
                    {t('auth.forgotPasswordFlow.passwordsDoNotMatch')}
                  </p>
                )}
              </div>

              {error && (
                <div className="text-sm text-destructive text-center bg-destructive/10 rounded-lg p-3 break-words">
                  {error}
                </div>
              )}

              {/* Clerk Smart CAPTCHA widget mount point for bot protection */}
              <div id="clerk-captcha" className="mt-2 flex justify-center" />

              <Button
                type="submit"
                className="w-full h-12 rounded-full text-sm font-medium"
                disabled={!isResetFormValid || isLoading}
              >
                {isLoading ? (
                  <CircularLoader className="size-4" />
                ) : (
                  <>
                    {t('auth.forgotPasswordFlow.resetPassword')}
                    <ArrowRight className="size-4" />
                  </>
                )}
              </Button>
            </form>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
